import { useMemo } from "react";
import { Helmet } from "@/lib/helmet";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLangInfo, withLangPrefix } from "@/i18n";

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? "https://traffynity.com").replace(/\/$/, "");
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/services/default-hero.avif`;
const BRAND_NAME = "Traffynity";
const BRAND_TWITTER = "@flowbankca";

const servicePageKeyByPath: Record<string, string> = {
  "/activity": "activity",
  "/certification": "certification",
  "/consulting": "consulting",
  "/courses": "courses",
  "/ecommerce": "ecommerce",
  "/interview": "interview",
  "/ip-incubation": "ipIncubation",
  "/matrix": "matrix",
  "/operations": "operations",
  "/team-expression": "teamExpression",
  "/web-dev": "webDev",
};

const SeoHead = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { lang, normalizedPath } = getLangInfo(pathname);

  const seoData = useMemo(() => {
    const isHome = normalizedPath === "/";
    const isContact = normalizedPath === "/contact";
    const serviceKey = servicePageKeyByPath[normalizedPath];
    const isKnown = isHome || isContact || Boolean(serviceKey);

    let title =
      lang === "zh-CN"
        ? "Traffynity | 北美华人短视频增长解决方案"
        : "Traffynity | Short-Form Growth System for Chinese Brands in North America";
    let description =
      lang === "zh-CN"
        ? "Traffynity 提供自媒体课程、短视频运营、IP孵化、矩阵运营与网站增长系统。"
        : "Traffynity delivers influence architecture: camp, short-form operations, IP incubation, matrix growth, and web infrastructure.";

    if (isHome) {
      const homeTitle = `${t("home.hero.titleLine1")} ${t("home.hero.titleEmphasis")}`.trim();
      title = `${homeTitle} | ${BRAND_NAME}`;
      description = t("home.hero.description");
    } else if (serviceKey) {
      title = `${t(`pages.${serviceKey}.title`)} | ${BRAND_NAME}`;
      description = t(`pages.${serviceKey}.subtitle`);
    } else if (isContact) {
      title = `${t("pages.contact.title")} | ${BRAND_NAME}`;
      description = t("pages.contact.subtitle");
    } else {
      title = lang === "zh-CN" ? `页面不存在 | ${BRAND_NAME}` : `Page Not Found | ${BRAND_NAME}`;
      description = lang === "zh-CN" ? "你访问的页面不存在。请返回首页继续浏览。" : "The page you requested does not exist. Return to the homepage.";
    }

    const canonicalZh = `${SITE_URL}${withLangPrefix(normalizedPath, "zh-CN")}`;
    const canonicalEn = `${SITE_URL}${withLangPrefix(normalizedPath, "en")}`;
    const canonicalUrl = lang === "en" ? canonicalEn : canonicalZh;

    return {
      title,
      description,
      canonicalUrl,
      canonicalZh,
      canonicalEn,
      lang,
      isHome,
      isKnown,
      serviceKey,
    };
  }, [lang, normalizedPath, t]);

  const geoAddress = {
    streetAddress: import.meta.env.VITE_ORG_STREET ?? "",
    addressLocality: import.meta.env.VITE_ORG_CITY ?? "",
    addressRegion: import.meta.env.VITE_ORG_REGION ?? "",
    postalCode: import.meta.env.VITE_ORG_POSTAL ?? "",
    addressCountry: import.meta.env.VITE_ORG_COUNTRY ?? "",
  };

  const hasAddress = Object.values(geoAddress).some((value) => value?.trim());
  const areaServed = (import.meta.env.VITE_ORG_AREASERVED ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const contactPhone = import.meta.env.VITE_ORG_PHONE ?? "";

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND_NAME,
    url: SITE_URL,
    email: "info@traffynity.com",
    logo: `${SITE_URL}/favicon.ico`,
    ...(hasAddress
      ? {
          address: {
            "@type": "PostalAddress",
            ...geoAddress,
          },
        }
      : {}),
    ...(areaServed.length
      ? {
          areaServed,
        }
      : {}),
    ...(contactPhone
      ? {
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: contactPhone,
              contactType: "sales",
            },
          ],
        }
      : {}),
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND_NAME,
    url: SITE_URL,
    inLanguage: seoData.lang,
    publisher: {
      "@type": "Organization",
      name: BRAND_NAME,
      url: SITE_URL,
    },
  };

  const webPageOrService = {
    "@context": "https://schema.org",
    "@type": seoData.serviceKey ? "Service" : "WebPage",
    name: seoData.title,
    description: seoData.description,
    url: seoData.canonicalUrl,
    inLanguage: seoData.lang,
    provider: {
      "@type": "Organization",
      name: BRAND_NAME,
      url: SITE_URL,
    },
  };

  const breadcrumb = seoData.isHome
    ? null
    : {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: SITE_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: seoData.title.replace(` | ${BRAND_NAME}`, ""),
            item: seoData.canonicalUrl,
          },
        ],
      };

  const faqItems = seoData.isHome
    ? (t("home.faq.items", { returnObjects: true }) as Array<{ q: string; summary: string }>)
    : [];

  const faq =
    seoData.isHome && faqItems.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.summary,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang={seoData.lang} />
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="application-name" content={BRAND_NAME} />
      <meta name="theme-color" content="#0c0f16" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="robots" content={seoData.isKnown ? "index,follow,max-image-preview:large" : "noindex,nofollow"} />

      <link rel="canonical" href={seoData.canonicalUrl} />
      <link rel="alternate" href={seoData.canonicalZh} hreflang="zh-CN" />
      <link rel="alternate" href={seoData.canonicalEn} hreflang="en" />
      <link rel="alternate" href={seoData.canonicalZh} hreflang="x-default" />

      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoData.canonicalUrl} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:alt" content={`${BRAND_NAME} cover image`} />
      <meta property="og:site_name" content={BRAND_NAME} />
      <meta property="og:locale" content={seoData.lang === "zh-CN" ? "zh_CN" : "en_US"} />
      <meta property="og:locale:alternate" content={seoData.lang === "zh-CN" ? "en_US" : "zh_CN"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:site" content={BRAND_TWITTER} />

      {seoData.serviceKey ? (
        <meta
          name="keywords"
          content={
            seoData.lang === "zh-CN"
              ? "短视频增长,IP孵化,SEO,AI可见性,品牌增长"
              : "short-form growth, IP incubation, SEO, AI visibility, brand growth"
          }
        />
      ) : null}

      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      <script type="application/ld+json">{JSON.stringify(website)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageOrService)}</script>
      {breadcrumb ? <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script> : null}
      {faq ? <script type="application/ld+json">{JSON.stringify(faq)}</script> : null}
    </Helmet>
  );
};

export default SeoHead;
